import {singleton} from "tsyringe";
import type {RichTextImageClickEvent} from "@o2d/rich-text-editor";
import {ImagePreviewHelper} from "../helpers/image-preview.helper";

/**
 * `RICH_TEXT_IMAGE_CLICK_EVENT` of `@o2d/rich-text-editor`, written out: a runtime
 * import of the package registers its custom element, and `services/index.ts` (which
 * resolves this listener) is loaded by many tests in the 'node' environment, which
 * has no DOM for that. `satisfies` ties the name to the package's `DocumentEventMap`
 * entry, so a typo or a renamed event fails a type check (TS1360, measured
 * 2026-09-26). Note: the vite build does not type-check, and the project-wide
 * `tsc -p .` currently stops at TS6305; it takes a direct `tsc` on this file.
 */
const RICH_TEXT_IMAGE_CLICK_EVENT = "rich-text-image-click" satisfies keyof DocumentEventMap;

/**
 * Handles `rich-text-image-click`, which `<o2d-rich-text-editor>` dispatches when an
 * image is clicked in view mode, or double-clicked in edit mode (Anke, 2026-09-26):
 * opens the enlarged preview.
 *
 * - An image inside a link (the external link tile wraps its text in
 *   `<a href target="_blank">`) keeps the link: nothing is opened and the event is
 *   not cancelled, so the click follows the link.
 * - Otherwise the event is cancelled. That tells the element the click is handled;
 *   it then stops the original click, so a clickable task card or tile around the
 *   text does not open as well.
 * - The editor element is the preview's owner: when it leaves the document (its
 *   dialog closes), the preview closes with it. The event is dispatched on that
 *   element, in the Light DOM, so `event.target` is the element itself.
 *
 * Exported apart from the class so it can be tested without a `document`.
 */
export function onRichTextImageClick(event: RichTextImageClickEvent, open = ImagePreviewHelper.open): void {
    if (ImagePreviewHelper.isInLink(event.composedPath())) {
        return;
    }
    const owner = event.target as { readonly isConnected: boolean } | null;
    if (!owner) {
        return;
    }
    event.preventDefault();
    open(event.detail.src, event.detail.alt ?? "", owner);
}

/**
 * Registers {@link onRichTextImageClick} once on `document`, for every rich text in the
 * app — read-only ones (`ha-quill-viewer` in task list, previews and tiles,
 * `ha-dialog-rte-view`, `ha-rte` when readonly, `knowledge-rte` in view mode) and, on a
 * double click, every editor: `ha-rte` in the issue dialog's description and in the
 * link tile dialog's text (both on purpose, Anke 2026-09-27, review 0048/01) and
 * `knowledge-rte` while editing. Resolved once in `services/index.ts`, like every
 * singleton service here.
 */
@singleton()
export class ImagePreviewListener {

    constructor() {
        document.addEventListener(RICH_TEXT_IMAGE_CLICK_EVENT, onRichTextImageClick);
    }

}
